import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useEvent } from '../contexts/EventContext';
import { useAuth } from '../contexts/AuthContext';
import { RegistrationSettingsContent } from './EventRegistrationSettings';
import { RegistrationResponsesContent } from './EventRegistrations';
import { EventPromoCodesContent } from './EventPromoCodes';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Eye } from 'lucide-react';
import { PUBLIC_SITE_URL } from '../config/api';
import apiService from '../services/api.service';

const Registrations: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedEventId, selectedEvent } = useEvent();
  const [searchParams] = useSearchParams();
  // Onglets à plat (5) : Général / Catégories / Questions & décharge / Réponses / Codes Promo.
  // 'config' (deep-link legacy) → 'general'.
  const [tab, setTab] = useState(() => {
    const p = searchParams.get('tab');
    return !p || p === 'config' ? 'general' : p;
  });

  const isAdmin = user?.role === 'admin';

  // Flag concours (admin uniquement) — initialisé depuis l'événement sélectionné.
  const [contestEnabled, setContestEnabled] = useState(false);
  const [contestSaving, setContestSaving] = useState(false);
  useEffect(() => {
    setContestEnabled(!!selectedEvent?.contest_enabled);
  }, [selectedEventId, selectedEvent?.contest_enabled]);

  const toggleContest = async (enabled: boolean) => {
    if (!selectedEventId) return;
    setContestEnabled(enabled); // optimiste
    setContestSaving(true);
    try {
      await apiService.put(`/events/${selectedEventId}/contest`, { enabled });
      toast.success(t('registrations.contestSaved'));
    } catch (err) {
      setContestEnabled(!enabled); // revert
      toast.error(t('registrations.contestError'));
    } finally {
      setContestSaving(false);
    }
  };

  // Ouvre le parcours d'inscription public en mode aperçu (aucune inscription/paiement réel).
  const openPreview = () => {
    if (!selectedEvent?.slug) return;
    const url = `${PUBLIC_SITE_URL}/events/${selectedEvent.slug}/register?preview=1`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!selectedEventId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t('registrations.noEventSelected')}
        </h2>
        <p className="text-muted-foreground">
          {t('registrations.selectEventHint')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('registrations.title')} — {selectedEvent?.name}
        </h1>
        <Button
          variant="outline"
          onClick={openPreview}
          disabled={!selectedEvent?.slug}
          title={t('registrations.previewHint')}
        >
          <Eye className="h-4 w-4 mr-2" />
          {t('registrations.preview')}
        </Button>
      </div>

      {/* Flag concours — réservé à l'admin LogFit */}
      {isAdmin && (
        <Card className="border-primary/40">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Switch
                id="contest_enabled"
                checked={contestEnabled}
                disabled={contestSaving}
                onCheckedChange={toggleContest}
              />
              <Label htmlFor="contest_enabled" className="cursor-pointer">
                {t('registrations.contestLabel')}
                <span className="ml-2 text-xs text-muted-foreground">(Admin LogFit)</span>
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="general">Général</TabsTrigger>
          <TabsTrigger value="categories">Catégories</TabsTrigger>
          <TabsTrigger value="questions">Questions &amp; décharge</TabsTrigger>
          <TabsTrigger value="responses">{t('registrations.tabs.responses')}</TabsTrigger>
          <TabsTrigger value="promo">{t('registrations.tabs.promoCodes')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Contenu rendu hors TabsContent : RegistrationSettingsContent reste UNE seule
          instance pour les 3 sections config (formData + sauvegarde unique préservés). */}
      <div className="mt-6">
        {(tab === 'general' || tab === 'categories' || tab === 'questions') && (
          <RegistrationSettingsContent
            eventId={selectedEventId}
            embedded
            section={tab as 'general' | 'categories' | 'questions'}
          />
        )}
        {tab === 'responses' && (
          <RegistrationResponsesContent eventId={selectedEventId} embedded />
        )}
        {tab === 'promo' && (
          <EventPromoCodesContent eventId={selectedEventId} embedded />
        )}
      </div>
    </div>
  );
};

export default Registrations;
